import ContinuationDate from './ContinuationDate';
import { SurfaceItemState } from '../interfaces/SurfaceItemState';
import { IStyle } from '@uifabric/styling';
import { IsFocusVisibleClassName } from '@uifabric/utilities';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { getCharmForId } from 'owa-calendar-charms-store';
import { CharmInfo } from 'owa-calendar-charms-store';
import { getOptimalTextColor } from 'owa-color-utils/lib/OptimalTextColorCache';
import { ControlIcons } from 'owa-control-icons';
import CalendarItemTypeConstants from '../store/schema/CalendarItemType';
import { ResponseTypeType } from '../store/schema/ResponseTypeType';
import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { CharmControlColorPattern } from 'owa-charmcontrol';
import { CharmControl } from 'owa-charmcontrol';
import { CharmControlColorProps } from 'owa-charmcontrol';
import ColorScheme, { colorSchemeToCss } from 'owa-color-utils/lib/ColorScheme';
import { addOpacityToColor, computeResultingOverlapColor } from 'owa-color-utils/lib/colorUtils';
import { addMilliseconds } from 'owa-datetime';
import { getPaletteAsRawColors } from 'owa-theme';
import { isPastTime } from 'owa-observable-datetime';
import * as React from 'react';
import {
    BORDER_OPACITY,
    OPACITY_FREE,
    OPACITY_TENTATIVE,
    OPACITY_BUSY,
    OPACITY_INCREMENT,
    MINIMUM_TEXT_CONTRAST_REST,
    MINIMUM_TEXT_CONTRAST_SELECTED,
} from '../utils/constants';
import {
    getFreeBusyItemBarClass,
    getFreeBusyItemBarStyling,
    getFreeBusyItemWrapperClass,
    FreeBusyType,
    FreeBusyConstants,
} from 'owa-freebusy-style';
import { getCalendarEventIcons } from 'owa-calendar-event-icons';

let classNames = require('classnames');

/**
 * Renders the free busy bar for the given item, using duration and free busy type
 * @param item The item to render the free busy bar for
 */
export function renderFreeBusyBar(
    freeBusyType: FreeBusyType,
    duration: number,
    colorScheme: ColorScheme
) {
    if (freeBusyType == null) {
        // don't render if there's no free busy type
        return null;
    }
    let freeBusySpecificClass = getFreeBusyItemBarClass(freeBusyType);

    let freeBusyStyling = getFreeBusyItemBarStyling(freeBusyType, colorScheme, duration);

    return <div className={freeBusySpecificClass} style={freeBusyStyling} />;
}

/**
 * Renders the charm wrapper for the given item
 * @param item The item being rendered
 * @param styles optional separate style sheet to use
 */
export function renderCharmWrapper(
    item: CalendarEvent,
    colorScheme: ColorScheme,
    charmId: number,
    styles?: any
) {
    if (!styles) {
        styles = require('./CalendarSurfaceItem.scss');
    }

    let charm = getCharmForId(charmId) as CharmInfo;

    let colorProps: CharmControlColorProps = {
        colorPattern: CharmControlColorPattern.TextColor,
        calPrimary: colorScheme.calPrimary,
        calDark: colorScheme.calDark,
        calDarkAlt: colorScheme.calDarkAlt,
        textColor: colorScheme.accessibleText.calPrimaryBackground,
        inheritTextColor: true,
    };

    return (
        charm.IconId > 0 &&
        item.CalendarItemType !== CalendarItemTypeConstants.Exception && (
            <div className={styles.charmWrapper}>
                <div className={styles.calendarIcon} title={charm.Name}>
                    <CharmControl
                        iconId={charm.IconId}
                        className={styles.surfaceRowCharm}
                        colorProps={colorProps}
                    />
                </div>
            </div>
        )
    );
}

/**
 * Renders the charm wrapper for the given all day item
 * @param item The item being rendered
 */
export function renderCharmWrapperAllDayItem(
    item: CalendarEvent,
    colorScheme: ColorScheme,
    charmId: number
) {
    let allDayStyles = require('./CalendarAllDaySurfaceItem.scss');
    return renderCharmWrapper(item, colorScheme, charmId, allDayStyles);
}

/**
 * Renders the icon wrapper for the given item
 * @param item The item being rendered
 * @param isShorter whether this item is using shorter item styling
 * @param styles optional separate style sheet to use
 */
export function renderIconWrapper(
    item: CalendarEvent,
    isShorter: boolean,
    itemState?: SurfaceItemState,
    styles?: any
): JSX.Element {
    if (!hasIcon(item, itemState)) {
        return null;
    }

    if (!styles) {
        styles = require('./CalendarSurfaceItem.scss');
    }

    const iconWrapperClass = isShorter ? styles.shorterItemIconWrapper : styles.iconWrapper;
    const children = getCalendarEventIcons(
        item.HasAttachments,
        item.CalendarItemType === CalendarItemTypeConstants.Occurrence,
        item.Sensitivity === 'Private',
        item.CalendarItemType === CalendarItemTypeConstants.Exception
    ).map((props, index) => <Icon {...props} className={styles.icon} key={index} />);

    if (itemState && itemState.continuesRight) {
        children.push(
            <div className={styles.icon} key={'continuesRightIcon'}>
                <ContinuationDate date={addMilliseconds(item.End, -1)} />
                <Icon
                    iconName={ControlIcons.ChevronRight}
                    className={classNames(styles.icon, styles.continuation)}
                />
            </div>
        );
    }

    return children.length == 0 ? null : <div className={iconWrapperClass}>{children}</div>;
}

/**
 * Renders the icon wrapper for the given all day item
 * @param item The item being rendered
 */
export function renderIconWrapperAllDayItem(item: CalendarEvent, itemState: SurfaceItemState) {
    let allDayStyles = require('./CalendarAllDaySurfaceItem.scss');
    return renderIconWrapper(item, false, itemState, allDayStyles);
}

/**
 * Gets the class for the entire item based on the free busy status
 */
export function getItemWrapperClass(item: CalendarEvent, dimPastEvents: boolean) {
    let itemWrapperClassNames = classNames(
        getItemSubjectClass(item),
        getItemIsPastClass(item, dimPastEvents),
        getFreeBusyItemWrapperClass(item.FreeBusyType)
    );
    return itemWrapperClassNames;
}

/**
 * Gets the class for the entire item based on the current time compared to the event end
 */
export function getItemIsPastClass(item: CalendarEvent, dimPastEvents: boolean) {
    let styles = require('./CalendarSurfaceItem.scss');
    return isPastTime(item.End) && dimPastEvents ? styles.itemHasEnded : null;
}

/**
 * Gets the class for the entire item based on the isCancelled status
 */
function getItemSubjectClass(item: CalendarEvent) {
    let styles = require('./CalendarSurfaceItem.scss');
    return item.IsCancelled || item.ResponseType == 'Decline' ? styles.itemIsCancelled : null;
}

/**
 * Gets the styles for just the content of the item, not including the free busy bar
 * @param freeBusyType the item free busy type
 * @param responseType the item response type
 * @param colorScheme the item color scheme
 */
export function getItemContentStyles(
    freeBusyType: FreeBusyType,
    responseType: ResponseTypeType,
    colorScheme: ColorScheme,
    isCancelled: boolean
): IStyle {
    let colorCssNormal = colorSchemeToCss(colorScheme);
    let backgroundColor = colorCssNormal.backgroundColor;
    let textColor = colorCssNormal.color;
    let minimumTextContrastRest = MINIMUM_TEXT_CONTRAST_REST;
    let minimumTextContrastSelected = MINIMUM_TEXT_CONTRAST_SELECTED;

    let opacity = calculateOpacity(freeBusyType);
    let borderWidth = calculateBorderWidth(freeBusyType);
    let rawPalette = getPaletteAsRawColors();

    /**
     * Calculate the background colors for the different interactive states, including their opacity
     */
    let transparentBackground = addOpacityToColor(backgroundColor, opacity);
    let borderBackground = transparentBackground;
    let hoverBackground = addOpacityToColor(backgroundColor, opacity + OPACITY_INCREMENT);
    let pressedBackground = addOpacityToColor(backgroundColor, 1);

    /**
     * Since the background colors have opacity, compute the RESULTING color from overlaying that opacity with the surface ms-color-white background
     * so that we know what color we're working with when computing an accessible text color
     */
    let totalBackgroundColor = computeResultingOverlapColor(
        transparentBackground,
        rawPalette.white
    );
    let totalHoverBackgroundColor = computeResultingOverlapColor(hoverBackground, rawPalette.white);
    let totalPressedBackgroundColor = computeResultingOverlapColor(
        pressedBackground,
        rawPalette.white
    );

    /**
     * Using the calculated true background colors, calculate accessible text colors
     */
    textColor = getOptimalTextColor(
        totalBackgroundColor,
        colorScheme,
        rawPalette.white,
        minimumTextContrastRest
    );
    let hoveredTextColor = getOptimalTextColor(
        totalHoverBackgroundColor,
        colorScheme,
        textColor,
        minimumTextContrastRest
    );
    let pressedTextColor = getOptimalTextColor(
        totalPressedBackgroundColor,
        colorScheme,
        textColor,
        minimumTextContrastSelected
    );

    /**
     * if item not responded to, use tentative styling with dashed border
     */
    let borderStyle = 'solid';
    let borderOpacity = opacity == 0 ? OPACITY_BUSY : opacity;
    if (responseType == 'NoResponseReceived' && !isCancelled) {
        borderStyle = 'dashed';
        borderBackground = backgroundColor;
        borderWidth = 1;
        borderOpacity = BORDER_OPACITY;
    }

    return {
        backgroundColor: transparentBackground,
        borderColor: addOpacityToColor(borderBackground, borderOpacity),
        borderWidth: borderWidth,
        borderStyle: borderStyle,
        color: textColor,
        fill: textColor,
        selectors: {
            '&:hover, .itemHovered &, .itemFocused &': {
                backgroundColor: hoverBackground,
                borderColor: addOpacityToColor(borderBackground, borderOpacity),
                color: hoveredTextColor,
                fill: hoveredTextColor,
            },
            '&:active, .itemFocused &:active, .itemSelected &': {
                backgroundColor: pressedBackground,
                borderColor: addOpacityToColor(pressedBackground, borderOpacity),
                color: pressedTextColor,
                fill: pressedTextColor,
            },
        },
    };
}

/**
 * Gets the styles for just the content of the item, not including the free busy bar
 * @param freeBusyType the item free busy type
 * @param responseType the item response type
 * @param colorScheme the item color scheme
 */
export function getMonthItemContentStyles(
    freeBusyType: FreeBusyType,
    responseType: ResponseTypeType,
    colorScheme: ColorScheme
): IStyle {
    let colorCssNormal = colorSchemeToCss(colorScheme);
    let backgroundColor = colorCssNormal.backgroundColor;
    let rawPalette = getPaletteAsRawColors();
    let textColor = colorCssNormal.color;
    let minimumTextContrastRest = MINIMUM_TEXT_CONTRAST_REST;
    let minimumTextContrastSelected = MINIMUM_TEXT_CONTRAST_SELECTED;
    let opacity = OPACITY_FREE;

    switch (freeBusyType) {
        case FreeBusyConstants.Busy:
        case FreeBusyConstants.OOF:
            opacity = OPACITY_BUSY;
            break;
        default:
            opacity = OPACITY_FREE;
    }

    let transparentBackground = addOpacityToColor(backgroundColor, opacity);

    let totalBackgroundColor = computeResultingOverlapColor(
        transparentBackground,
        rawPalette.white
    );
    textColor = getOptimalTextColor(
        totalBackgroundColor,
        colorScheme,
        rawPalette.white,
        minimumTextContrastRest
    );
    let hoverBackground = addOpacityToColor(backgroundColor, opacity + OPACITY_INCREMENT);
    let totalHoverBackgroundColor = computeResultingOverlapColor(hoverBackground, rawPalette.white);
    let hoveredTextColor = getOptimalTextColor(
        totalHoverBackgroundColor,
        colorScheme,
        textColor,
        minimumTextContrastRest
    );
    let pressedBackground = addOpacityToColor(backgroundColor, 1);
    let totalPressedBackgroundColor = computeResultingOverlapColor(
        pressedBackground,
        rawPalette.white
    );
    let pressedTextColor = getOptimalTextColor(
        totalPressedBackgroundColor,
        colorScheme,
        textColor,
        minimumTextContrastSelected
    );

    return {
        backgroundColor: transparentBackground,
        borderColor: 'transparent',
        color: textColor,
        fill: textColor,
        borderStyle: 'solid',
        selectors: {
            '&:hover, .itemHovered &': {
                backgroundColor: hoverBackground,
                color: hoveredTextColor,
                fill: hoveredTextColor,
            },
            [`.${IsFocusVisibleClassName} &:focus, .itemFocused &`]: {
                backgroundColor: hoverBackground,
                borderColor: pressedBackground,
                color: hoveredTextColor,
                fill: hoveredTextColor,
            },
            '&:active, .itemSelected &': {
                backgroundColor: pressedBackground,
                borderColor: pressedBackground,
                color: pressedTextColor,
                fill: pressedTextColor,
            },
        },
    };
}

/**
 * Returns true if the item being rendered has any icons to display
 * @param item the item being rendered
 */
export function hasIcon(item: CalendarEvent, itemState: SurfaceItemState): boolean {
    return (
        item.CalendarItemType == CalendarItemTypeConstants.Occurrence ||
        item.CalendarItemType == CalendarItemTypeConstants.Exception ||
        item.HasAttachments ||
        item.Sensitivity == 'Private' ||
        (itemState && itemState.continuesRight)
    );
}

function calculateOpacity(freeBusyType: FreeBusyType): number {
    let opacity = OPACITY_BUSY;

    switch (freeBusyType) {
        case FreeBusyConstants.Tentative:
            opacity = OPACITY_TENTATIVE;
            break;
        case FreeBusyConstants.Free:
        case FreeBusyConstants.WorkingElsewhere:
            opacity = OPACITY_FREE;
    }

    return opacity;
}

function calculateBorderWidth(freeBusyType: FreeBusyType): number {
    switch (freeBusyType) {
        case FreeBusyConstants.Free:
        case FreeBusyConstants.WorkingElsewhere:
            return 1;
    }

    return 0;
}
